import { API_BASE_URL } from '../config';
import SearchBox, { loadDoctorOptions, loadPatientOptions } from '../components/SearchBox';
import AppointmentCalendar, { appointmentDayKey, slotRange } from '../components/AppointmentCalendar';
import React, { useState, useEffect } from 'react';

const VISIT_MINUTES = 30;

const AppointmentScheduling = () => {
  const [appointmentData, setAppointmentData] = useState({
    patientId: '',
    doctorId: '',
    appointmentDate: '',
    appointmentTime: '',
    reason: '',
    appointmentPlace: '',
  });
  const [doctors, setDoctors] = useState([]);
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);
  const [selectedPatientName, setSelectedPatientName] = useState('');
  const [doctorSearchTerm, setDoctorSearchTerm] = useState('');
  const [doctorSearchResults, setDoctorSearchResults] = useState([]);
  const [doctorSearchLoading, setDoctorSearchLoading] = useState(false);
  const [selectedDoctorName, setSelectedDoctorName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [doctorAppointments, setDoctorAppointments] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState('');

  useEffect(() => {
    // Original fetchDoctors logic, which will be modified for search later
    const fetchDoctors = async () => {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Authentication token not found. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/doctors`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const data = await response.json();
        console.log('Doctors API response:', data);
        if (response.ok) {
          setDoctors(Array.isArray(data) ? data : []);
        } else {
          setError(data.message || 'Failed to fetch doctors.');
        }
      } catch (err) {
        setError(`Network error: ${err.message}`);
      }
      setLoading(false);
    };
    fetchDoctors();
  }, []);

  // Debounce effect for patient search
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (patientSearchTerm.length > 2) {
        setPatientSearchLoading(true);
        const token = localStorage.getItem('token');
        try {
          const response = await fetch(
            `${API_BASE_URL}/patients/search?name=${patientSearchTerm}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const data = await response.json();
          if (response.ok) {
            setPatientSearchResults(Array.isArray(data) ? data : []);
          } else {
            setError(data.message || 'Failed to search patients.');
          }
        } catch (err) {
          setError(`Network error: ${err.message}`);
        } finally {
          setPatientSearchLoading(false);
        }
      } else {
        setPatientSearchResults([]);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [patientSearchTerm]);

  // Debounce effect for doctor search
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (doctorSearchTerm.length > 2) {
        setDoctorSearchLoading(true);
        const token = localStorage.getItem('token');
        try {
          const response = await fetch(
            `${API_BASE_URL}/doctors/search?name=${doctorSearchTerm}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const data = await response.json();
          if (response.ok) {
            setDoctorSearchResults(Array.isArray(data) ? data : []);
          } else {
            setError(data.message || 'Failed to search doctors.');
          }
        } catch (err) {
          setError(`Network error: ${err.message}`);
        } finally {
          setDoctorSearchLoading(false);
        }
      } else {
        setDoctorSearchResults([]);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [doctorSearchTerm]);

  useEffect(() => {
    if (!appointmentData.doctorId) {
      setDoctorAppointments([]);
      setCalendarError('');
      return undefined;
    }
    let ignore = false;
    const loadCalendar = async () => {
      setCalendarLoading(true);
      setCalendarError('');
      const token = localStorage.getItem('token');
      try {
        const response = await fetch(`${API_BASE_URL}/appointments?doctorId=${appointmentData.doctorId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (ignore) return;
        if (response.ok) setDoctorAppointments(Array.isArray(data) ? data : []);
        else setCalendarError(data.message || 'Could not load this doctor’s calendar.');
      } catch (err) {
        if (!ignore) setCalendarError(`Network error: ${err.message}`);
      } finally {
        if (!ignore) setCalendarLoading(false);
      }
    };
    loadCalendar();
    return () => { ignore = true; };
  }, [appointmentData.doctorId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'patientSearch') {
      setPatientSearchTerm(value);
      if (appointmentData.patientId && !value) { // Clear selected patient if search input is cleared
        setAppointmentData(prevData => ({ ...prevData, patientId: '' }));
        setSelectedPatientName('');
      }
    } else if (name === 'doctorSearch') {
      setDoctorSearchTerm(value);
      if (appointmentData.doctorId && !value) { // Clear selected doctor if search input is cleared
        setAppointmentData(prevData => ({ ...prevData, doctorId: '' }));
        setSelectedDoctorName('');
      }
    } else {
      setAppointmentData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleSelectPatient = (patient) => {
    setAppointmentData(prevData => ({ ...prevData, patientId: patient._id }));
    setSelectedPatientName(patient.PATIENT_NAME); // Assuming patient has a PATIENT_NAME property
    setPatientSearchTerm(''); // Clear search term after selection
    setPatientSearchResults([]); // Clear search results after selection
  };

  const handleSelectDoctor = (doctor) => {
    setAppointmentData(prevData => ({ ...prevData, doctorId: doctor._id }));
    setSelectedDoctorName(doctor.DOCTOR_NAME); // Assuming doctor has a DOCTOR_NAME property
    setDoctorSearchTerm(''); // Clear search term after selection
    setDoctorSearchResults([]); // Clear search results after selection
  };

  const chosenMinutes = minutesFromClock(appointmentData.appointmentTime);
  const chosenRange = chosenMinutes == null ? null : { start: chosenMinutes, end: chosenMinutes + VISIT_MINUTES };
  const slotConflict = chosenRange && appointmentData.appointmentDate
    ? doctorAppointments.find((appointment) => {
      if (appointment.APPOINTMENT_STATUS === 'Cancelled') return false;
      if (appointmentDayKey(appointment.APPOINTMENT_DATE) !== appointmentData.appointmentDate) return false;
      const booked = slotRange(appointment.APPOINTMENT_SLOT);
      return booked && chosenRange.start < booked.end && booked.start < chosenRange.end;
    })
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    const token = localStorage.getItem('token');

    if (!token) {
      setError('Authentication token not found. Please log in again.');
      setLoading(false);
      return;
    }

    // Construct the appointment data to send to the backend
    const appointmentToSend = {
      PATIENT_ID: appointmentData.patientId,
      DOCTOR_ID: appointmentData.doctorId,
      APPOINTMENT_DATE: appointmentData.appointmentDate, // Already YYYY-MM-DD format
      APPOINTMENT_SLOT: formatSlot(appointmentData.appointmentTime),
      APPOINTMENT_PLACE: appointmentData.appointmentPlace, // New field
      DOCTOR_NAME: selectedDoctorName, // New field
      PATIENT_SYMPTOMS: appointmentData.reason, // Renamed from 'reason'
      // Other optional fields can be added here if needed
    };

    // Basic client-side validation for required fields
    const requiredFields = ['PATIENT_ID', 'DOCTOR_ID', 'APPOINTMENT_DATE', 'APPOINTMENT_SLOT', 'APPOINTMENT_PLACE'];
    for (const field of requiredFields) {
      if (!appointmentToSend[field]) {
        setError(`Please fill in the ${field.replace(/([A-Z_])/g, ' $1').toLowerCase().replace(/_ /g, '')} field.`);
        setLoading(false);
        return;
      }
    }

    if (slotConflict) {
      setError(`${selectedDoctorName || 'This doctor'} is already booked at ${slotConflict.APPOINTMENT_SLOT} with ${slotConflict.PATIENT_ID?.PATIENT_NAME || 'another patient'}. Choose an open time on the calendar.`);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(appointmentToSend),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message || 'Appointment scheduled successfully!');
        // Optionally clear the form after successful scheduling
        setAppointmentData({
          patientId: '',
          doctorId: '',
          appointmentDate: '',
          appointmentTime: '',
          reason: '',
          appointmentPlace: '',
        });
        setSelectedPatientName('');
        setSelectedDoctorName('');
      } else {
        setError(data.message || 'Failed to schedule appointment.');
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    }
    setLoading(false);
  };

  const handleSlotSelect = ({ date, minutes }) => {
    setAppointmentData((current) => ({
      ...current,
      appointmentDate: date,
      appointmentTime: `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
    }));
    setError('');
  };

  const handleBookedSelect = (appointment) => {
    const name = appointment.PATIENT_ID?.PATIENT_NAME || 'A patient';
    setError(`${name} is already booked at ${appointment.APPOINTMENT_SLOT || 'that time'}. Pick an open time on the calendar.`);
  };

  return (
    <div style={{ maxWidth: 1100, margin: '2rem auto', padding: '2rem', border: '1px solid #ccc', borderRadius: 8 }}>
      <h2>Schedule Appointment</h2>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1rem' }}>
          <label>Patient Name:</label>
          <SearchBox
            name="patientSearch"
            value={selectedPatientName || patientSearchTerm}
            filterText={patientSearchTerm}
            onChange={handleChange}
            placeholder="Search for a patient by name"
            loadOptions={loadPatientOptions}
            onSelect={(option) => handleSelectPatient(option.item)}
          />
          {patientSearchLoading && <p>Searching patients...</p>}
          {patientSearchResults.length > 0 && (
            <ul style={{ listStyleType: 'none', padding: 0, margin: '0.5rem 0', border: '1px solid #eee', maxHeight: '150px', overflowY: 'auto' }}>
              {patientSearchResults.map(patient => (
                <li
                  key={patient._id}
                  onClick={() => handleSelectPatient(patient)}
                  style={{
                    padding: '0.5rem',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    backgroundColor: appointmentData.patientId === patient._id ? '#f0f0f0' : 'transparent',
                  }}
                >
                  {patient.PATIENT_NAME} ({patient._id})
                </li>
              ))}
            </ul>
          )}
          {appointmentData.patientId && !patientSearchTerm && !patientSearchResults.length && (
            <p style={{ marginTop: '0.5rem' }}>Selected Patient: {selectedPatientName}</p>
          )}
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Doctor:</label>
          <SearchBox
            name="doctorSearch"
            value={selectedDoctorName || doctorSearchTerm}
            filterText={doctorSearchTerm}
            onChange={handleChange}
            placeholder="Search for a doctor by name"
            loadOptions={loadDoctorOptions}
            onSelect={(option) => handleSelectDoctor(option.item)}
          />
          {doctorSearchLoading && <p>Searching doctors...</p>}
          {doctorSearchResults.length > 0 && (
            <ul style={{ listStyleType: 'none', padding: 0, margin: '0.5rem 0', border: '1px solid #eee', maxHeight: '150px', overflowY: 'auto' }}>
              {doctorSearchResults.map(doctor => (
                <li
                  key={doctor._id}
                  onClick={() => handleSelectDoctor(doctor)}
                  style={{
                    padding: '0.5rem',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    backgroundColor: appointmentData.doctorId === doctor._id ? '#f0f0f0' : 'transparent',
                  }}
                >
                  {doctor.DOCTOR_NAME} ({doctor._id})
                </li>
              ))}
            </ul>
          )}
          {appointmentData.doctorId && !doctorSearchTerm && !doctorSearchResults.length && (
            <p style={{ marginTop: '0.5rem' }}>Selected Doctor: {selectedDoctorName}</p>
          )}
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Date:</label>
          <input
            type="date"
            name="appointmentDate"
            value={appointmentData.appointmentDate}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Time:</label>
          <input
            type="time"
            name="appointmentTime"
            value={appointmentData.appointmentTime}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem' }}
          />
          {appointmentData.appointmentTime && <p style={{ margin: '0.35rem 0 0', color: '#495057' }}>Visit length is 30 minutes: {formatSlot(appointmentData.appointmentTime)}</p>}
          {slotConflict && (
            <p style={{ margin: '0.35rem 0 0', color: '#dc3545' }}>
              {selectedDoctorName || 'This doctor'} is already booked then ({slotConflict.APPOINTMENT_SLOT}) with {slotConflict.PATIENT_ID?.PATIENT_NAME || 'another patient'}.
            </p>
          )}
          {chosenRange && appointmentData.appointmentDate && !slotConflict && !calendarLoading && appointmentData.doctorId && (
            <p style={{ margin: '0.35rem 0 0', color: '#1f4d40' }}>This time is free on the calendar.</p>
          )}
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Doctor’s calendar:</label>
          {!appointmentData.doctorId && <p style={{ color: '#6c757d' }}>Select a doctor, then click an open time to fill the slot.</p>}
          {appointmentData.doctorId && calendarLoading && <p>Loading {selectedDoctorName || 'the doctor'}’s calendar...</p>}
          {calendarError && <p style={{ color: 'red' }}>{calendarError}</p>}
          {appointmentData.doctorId && !calendarLoading && (
            <>
              <p style={{ marginTop: 0, color: '#495057' }}>Booked visits are shown below. Click an open time to select it.</p>
              <AppointmentCalendar
                appointments={doctorAppointments}
                onSelect={handleBookedSelect}
                onSlotSelect={handleSlotSelect}
                focusDate={appointmentData.appointmentDate}
                draft={chosenMinutes != null && appointmentData.appointmentDate ? { date: appointmentData.appointmentDate, minutes: chosenMinutes } : null}
                maxHeight={520}
              />
            </>
          )}
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Reason (Optional):</label>
          <textarea
            name="reason"
            value={appointmentData.reason}
            onChange={handleChange}
            style={{ width: '100%', padding: '0.5rem' }}
          ></textarea>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Appointment Place:</label>
          <input
            type="text"
            name="appointmentPlace"
            value={appointmentData.appointmentPlace}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}
        {success && <div style={{ color: 'green', marginBottom: '1rem' }}>{success}</div>}
        <button type="submit" style={{ padding: '0.5rem 1rem' }} disabled={loading}>
          {loading ? 'Scheduling...' : 'Schedule Appointment'}
        </button>
      </form>
    </div>
  );
};

function minutesFromClock(value) {
  const match = /^(\d{2}):(\d{2})$/.exec(value || '');
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

function clockText(minutes) {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const shown = hour % 12 || 12;
  return `${shown}:${String(minute).padStart(2, '0')} ${suffix}`;
}

function formatSlot(value) {
  const start = minutesFromClock(value);
  if (start == null) return value;
  return `${clockText(start)} - ${clockText(start + VISIT_MINUTES)}`;
}

export default AppointmentScheduling;
